import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Home, Search } from "lucide-react";
import SiteNavigation from "../components/SiteNavigation";
import FSBOHero from "../components/FSBOHero";
import HotMarkets from "../components/HotMarkets";
import DFWMetroMap from "../components/DFWMetroMap";
import TestimonialsSection from "../components/TestimonialsSection";
import SuccessStories from "../components/SuccessStories";

const RoleSelection = () => {
  const navigate = useNavigate();

  const selectRole = (role) => {
    navigate(`/register?role=${role}`);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Helmet>
        <title>Its-Sold.com™ - FREE FSBO Listings in Dallas-Fort Worth | Sell Your Home Without Agent Fees</title>
        <meta name="description" content="List your home for FREE on DFW's premier For Sale By Owner platform. Save $25K+ in commissions. No agent fees, ever. Serving Dallas, Fort Worth, Plano, Frisco & all DFW cities." />
        <link rel="canonical" href="https://its-sold.com/" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      
      {/* Consistent Navigation */}
      <SiteNavigation />

      {/* NEW Clean FSBO Hero */}
      <FSBOHero />

      {/* How It Works Section */}
      <section className="py-20 bg-white" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Sell Your Home in 3 Simple Steps
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              No agents. No commissions. Just a straightforward way to sell your home.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Create Your Listing",
                description: "Sign up free and add your home details, photos, and asking price. Takes just 10 minutes.",
                icon: "🏠"
              },
              {
                step: "2", 
                title: "Connect with Buyers",
                description: "Receive inquiries directly from interested buyers. You control who you talk to.",
                icon: "🤝"
              },
              {
                step: "3",
                title: "Close & Save Thousands",
                description: "Negotiate directly, close the deal, and keep the money you would have paid an agent.",
                icon: "💰"
              }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="relative bg-[#FAF9F6] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow duration-300 border border-gray-100"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-[#E07C4D] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {item.step}
                </div>
                <div className="text-4xl mb-4 mt-2">{item.icon}</div>
                <h3 className="text-xl font-bold text-[#1E3A5F] mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => selectRole('seller')}
              className="bg-gradient-to-r from-[#E07C4D] to-[#D16844] hover:from-[#D16844] hover:to-[#B55D35] text-white font-semibold py-6 px-10 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-lg"
              data-testid="how-it-works-cta"
            >
              Start Your Free Listing
            </Button>
          </div>
        </div>
      </section>

      {/* Role Selection - Simplified */}
      <section className="py-16 bg-gradient-to-b from-white to-[#FAF9F6]" data-testid="role-selection-section">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Are You Selling or Buying?
            </h2>
            <p className="text-lg text-gray-600 mb-6">Choose your path to get started</p>
            
            {/* No Agents Notice */}
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-red-50 border-2 border-red-200 rounded-full">
              <span className="text-red-600 font-bold text-lg">🚫</span>
              <span className="text-red-700 font-semibold text-sm sm:text-base">
                No Real Estate Agents Allowed to Post or Solicit Sellers
              </span>
            </div>
          </div>

          {/* Protection Features */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 mb-10">
            <h3 className="text-lg font-bold text-green-900 mb-4 text-center">🛡️ Seller Protection Guarantee</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                <span className="text-green-800"><strong>Agent-Free Zone</strong> - No solicitation allowed</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                <span className="text-green-800"><strong>TX License Verified</strong> - All agents must verify</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                <span className="text-green-800"><strong>Spam Blocked</strong> - Automated protection</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-600">✓</span>
                <span className="text-green-800"><strong>Since 1998</strong> - 27 years trusted</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Seller Card */}
            <div 
              onClick={() => selectRole('seller')}
              className="group cursor-pointer bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#E07C4D]"
              data-testid="seller-card"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#E07C4D] to-[#D16844] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Home className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1E3A5F]">I'm Selling</h3>
                  <p className="text-[#E07C4D] font-semibold">100% FREE</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-700">
                  <span className="text-green-500">✓</span> List your home for free
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <span className="text-green-500">✓</span> No agent commissions
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <span className="text-green-500">✓</span> Direct buyer contact
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <span className="text-green-500">✓</span> You keep 100% equity
                </li>
              </ul>
              <Button className="w-full bg-[#E07C4D] hover:bg-[#D16844] text-white font-semibold py-4 rounded-xl">
                List My Home Free →
              </Button>
            </div>

            {/* Buyer Card */}
            <div 
              onClick={() => selectRole('buyer')}
              className="group cursor-pointer bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#1E3A5F]"
              data-testid="buyer-card"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1E3A5F] to-[#162C4B] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Search className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1E3A5F]">I'm Buying</h3>
                  <p className="text-[#1E3A5F] font-semibold">Better Deals</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-700">
                  <span className="text-green-500">✓</span> Browse FSBO listings
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <span className="text-green-500">✓</span> No agent markups
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <span className="text-green-500">✓</span> Talk directly to owners
                </li>
                <li className="flex items-center gap-3 text-gray-700">
                  <span className="text-green-500">✓</span> Save on purchase price
                </li>
              </ul>
              <Button className="w-full bg-[#1E3A5F] hover:bg-[#162C4B] text-white font-semibold py-4 rounded-xl">
                Browse Homes →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <SuccessStories />

      {/* Testimonials */}
      <TestimonialsSection maxCards={6} />

      {/* DFW Metro Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <DFWMetroMap />
        </div>
      </section>

      {/* Hot Markets */}
      <HotMarkets />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#1E3A5F] to-[#162C4B]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Sell Your Home Without the Commission?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Join thousands of Texas homeowners who've saved money with FSBO.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => selectRole('seller')}
              className="bg-gradient-to-r from-[#E07C4D] to-[#D16844] hover:from-[#D16844] hover:to-[#B55D35] text-white font-semibold py-6 px-10 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-lg"
              data-testid="final-cta-seller"
            >
              List My Home FREE
            </Button>
            <Button
              onClick={() => navigate('/listings')}
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] font-semibold py-6 px-10 rounded-full transition-all duration-300 text-lg"
              data-testid="final-cta-browse"
            >
              Browse Listings
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoleSelection;
